import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Layout from '../components/Layout';
import ImageModal from '../components/ImageModal'; // Usaremos o modal de visualização
import { 
  Camera, 
  Upload, 
  Search, 
  Eye, 
  Trash2, 
  Download,
  Plus,
  FileImage,
  FileText,
  Box, // Ícone para STL (substituído de Cube)
  User
} from 'lucide-react';

// --- Componente do Modal de Upload ---
const UploadModal = ({ isOpen, onClose, patients, token, backendUrl, onSuccess }) => {
  const [selectedPatient, setSelectedPatient] = useState('');
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [detectedTypeLabel, setDetectedTypeLabel] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    if (!isOpen) {
      // Resetar o estado ao fechar o modal
      setSelectedPatient('');
      setFile(null);
      setDescription('');
      setNotes('');
      setUploading(false);
      setDetectedTypeLabel('');
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      setFile(null);
      setDetectedTypeLabel('');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    let detectedType = '';

    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
      detectedType = 'Imagem';
    } else if (fileExtension === 'pdf') {
      detectedType = 'Documento (PDF)';
    } else if (fileExtension === 'stl') {
      detectedType = 'Escaneamento intraoral (STL)';
    } else {
      showToast('error', 'Tipo de arquivo não suportado. Use JPG, PNG, PDF ou STL.');
      setFile(null);
      setDetectedTypeLabel('');
      e.target.value = null; // Limpa o input
      return;
    }

    // Limite de tamanho (35MB para STL, 15MB para outros)
    const maxSize = fileExtension === 'stl' ? 35 * 1024 * 1024 : 15 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
        showToast('error', `Arquivo muito grande. O limite é de ${maxSize / 1024 / 1024}MB.`);
        setFile(null);
        setDetectedTypeLabel('');
        e.target.value = null;
        return;
    }

    setFile(selectedFile);
    setDetectedTypeLabel(`Tipo detectado: ${detectedType}`);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedPatient || !file) {
      showToast('error', 'Por favor, selecione um paciente e um arquivo.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('patient_id', selectedPatient);
    formData.append('file', file);
    formData.append('description', description);
    formData.append('notes', notes);

    try {
      const response = await fetch(`${backendUrl}/api/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        showToast('success', 'Arquivo enviado com sucesso!');
        // Chama a função de sucesso para fechar o modal e recarregar a lista
        onSuccess();
      } else {
        const errorData = await response.json();
        showToast('error', errorData.detail || 'Erro ao enviar arquivo.');
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      showToast('error', 'Ocorreu um erro de rede. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
      onClick={onClose} // Fechar ao clicar fora
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b">
            <h3 className="text-xl font-semibold text-gray-800">Novo Arquivo</h3>
            <p className="text-sm text-gray-500 mt-1">Faça o upload de imagens, PDFs ou escaneamentos 3D (STL).</p>
        </div>
        <form onSubmit={handleUpload} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Paciente *</label>
            <select
              value={selectedPatient}
              onChange={(e) => setSelectedPatient(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Selecione um paciente</option>
              {patients.map((p) => (
                <option key={p.id} value={p.id}>{p.full_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Arquivo *</label>
            <input
              type="file"
              onChange={handleFileChange}
              accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.stl"
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              required
            />
            {detectedTypeLabel && (
                <p className="text-sm text-green-600 mt-2">{detectedTypeLabel}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Radiografia panorâmica inicial"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas Adicionais</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Observações clínicas, pedidos, etc."
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} disabled={uploading} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50">Cancelar</button>
            <button type="submit" disabled={uploading || !file} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {uploading ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Componente Principal da Página ---
const ImagesPage = () => {
  const { token } = useAuth();
  const { showToast } = useToast();
  
  const [images, setImages] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedPatientFilter, setSelectedPatientFilter] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const backendUrl =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8001'
    : '');

  // Atualizado para incluir o novo tipo
  const imageTypes = [
    { value: 'imagem', label: 'Imagem', icon: <Camera className="w-4 h-4 mr-2" /> },
    { value: 'documento', label: 'Documento/PDF', icon: <FileText className="w-4 h-4 mr-2" /> },
    { value: 'escaneamento', label: 'Escaneamento 3D', icon: <Box className="w-4 h-4 mr-2" /> },
  ];

  const fetchImages = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      let allImages = [];
      let page = 1;
      let keepGoing = true;

      while (keepGoing) {
        const response = await fetch(`${backendUrl}/api/images?lightweight=true&page=${page}&limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) {
          showToast('error', 'Falha ao carregar imagens.');
          break;
        }

        const data = await response.json();
        const rows = Array.isArray(data) ? data : [];

        allImages = [...allImages, ...rows];

        if (rows.length < 100) {
          keepGoing = false;
        } else {
          page += 1;
        }
      }

      setImages(allImages);
    } catch (error) {
      console.error('Erro ao carregar imagens:', error);
      showToast('error', 'Erro de rede ao carregar imagens.');
    } finally {
      setLoading(false);
    }
  }, [token, backendUrl, showToast]);

  const fetchPatients = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${backendUrl}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPatients(data);
      }
    } catch (error) {
      console.error('Erro ao carregar pacientes:', error);
    }
  }, [token, backendUrl]);

  useEffect(() => {
    fetchImages();
    fetchPatients();
  }, [fetchImages, fetchPatients]);

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    fetchImages(); // Recarrega a lista de imagens
  };

  const openImageModal = async (image) => {
    try {
      // O backend agora lida com o carregamento de dados do STL no get_image
      const response = await fetch(`${backendUrl}/api/images/${image.id}?include_data=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const imageData = await response.json();
        setSelectedImage(imageData);
        setShowImageModal(true);
      } else {
        showToast('error', 'Não foi possível carregar os dados do arquivo.');
      }
    } catch (error) {
      console.error('Erro ao carregar dados da imagem:', error);
      showToast('error', 'Erro de rede ao buscar detalhes do arquivo.');
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Tem certeza que deseja deletar este arquivo?')) return;

    try {
      const response = await fetch(`${backendUrl}/api/images/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        showToast('success', 'Arquivo deletado com sucesso!');
        setImages(images.filter(img => img.id !== imageId));
      } else {
        const errorData = await response.json();
        showToast('error', errorData.detail || 'Erro ao deletar arquivo.');
      }
    } catch (error) {
      console.error('Erro ao deletar imagem:', error);
      showToast('error', 'Erro de rede ao deletar arquivo.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Data desconhecida';
    return new Date(dateString).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };

  const filteredImages = images.filter(image => {
    const patientName = image.patient_name || '';
    const matchesSearch = searchQuery === '' || 
      (image.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (image.original_filename || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      patientName.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = selectedType === '' || image.image_type === selectedType;
    const matchesPatient = selectedPatientFilter === '' || image.patient_id === selectedPatientFilter;

    return matchesSearch && matchesType && matchesPatient;
  });

  const getFileIcon = (mimeType, imageType) => {
    if (imageType === 'escaneamento' || (mimeType || '').includes('stl')) {
      return <Box className="w-10 h-10 text-blue-500" />;
    }
    if ((mimeType || '').includes('pdf')) {
      return <FileText className="w-10 h-10 text-red-500" />;
    }
    return <FileImage className="w-10 h-10 text-gray-500" />;
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Galeria de Arquivos</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Gerencie imagens, documentos e escaneamentos dos seus pacientes.</p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="mt-4 sm:mt-0 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 shadow-sm"
          >
            <Plus className="w-5 h-5" />
            <span>Novo Arquivo</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por paciente, descrição..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os tipos</option>
              {imageTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <select
              value={selectedPatientFilter}
              onChange={(e) => setSelectedPatientFilter(e.target.value)}
              className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os pacientes</option>
              {patients.map(p => (
                <option key={p.id} value={p.id}>{p.full_name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Image Grid */}
        {loading ? (
          <div className="text-center py-10">Carregando...</div>
        ) : filteredImages.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {filteredImages.map(image => (
              <div key={image.id} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden group transition-shadow hover:shadow-xl">
                <div className="h-40 flex items-center justify-center bg-gray-100 relative">
                  {image.mime_type?.startsWith('image/') && image.image_data ? (
                    <img src={`data:${image.mime_type};base64,${image.image_data}`} alt={image.description || 'miniatura'} className="w-full h-full object-cover" />
                  ) : (
                    getFileIcon(image.mime_type, image.image_type)
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center space-x-3">
                    <button onClick={() => openImageModal(image)} className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"><Eye className="w-5 h-5" /></button>
                    <button onClick={() => deleteImage(image.id)} className="p-2 bg-white rounded-full text-red-600 hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-5 h-5" /></button>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-sm font-semibold text-gray-800 truncate" title={image.original_filename}>{image.original_filename}</p>
                  <p className="text-xs text-gray-600 flex items-center mt-1"><User className="w-3 h-3 mr-1.5" />{image.patient_name || 'Paciente não encontrado'}</p>
                  <p className="text-xs text-gray-500 mt-1">{formatDate(image.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-10 bg-white rounded-lg shadow-sm border">
            <p className="text-gray-600">Nenhum arquivo encontrado.</p>
            <p className="text-sm text-gray-500 mt-2">Tente ajustar os filtros ou adicione um novo arquivo.</p>
          </div>
        )}
      </div>

      {/* Modals */}
      <UploadModal 
        isOpen={showUploadModal} 
        onClose={() => setShowUploadModal(false)} 
        patients={patients} 
        token={token} 
        backendUrl={backendUrl} 
        onSuccess={handleUploadSuccess} 
      />
      
      {showImageModal && selectedImage && (
        <ImageModal 
          image={selectedImage} 
          isOpen={showImageModal} 
          onClose={() => setShowImageModal(false)} 
          formatDate={formatDate}
        />
      )}
    </Layout>
  );
};

export default ImagesPage;

